import { useState, useEffect, useRef } from "react";

interface DecimalInputProps {
    value: string;
    type: "text";
    inputMode: "decimal";
    onFocus: () => void;
    onKeyDown: (e: any) => void;
    onChange: (e: any) => void;
    onBlur: () => void;
}

// Keeps a decimal $/% input showing a clean 2-decimal value (e.g. "42.90") when idle,
// while preserving exactly what the user is typing (e.g. "42.9" or "20.") while focused,
// since <input type="number"> silently discards an in-progress value like "20." on every
// keystroke and a plain toFixed(2)-formatted controlled value fights typing a second decimal digit.
//
// `value` is the committed value as a string (pass "" for an empty/unset field). `onCommit`
// receives the new raw string on every valid keystroke, plus once more, clamped, if the max is
// exceeded.
export function useDecimalInput(
    value: string,
    onCommit: (raw: string) => void,
    max?: number
): DecimalInputProps {
    const format = (v: string) => (v === "" ? "" : Number(v).toFixed(2));
    const [display, setDisplay] = useState(() => format(value));
    const focusedRef = useRef(false);

    useEffect(() => {
        if (!focusedRef.current) {
            setDisplay(format(value));
        }
    }, [value]);

    return {
        value: display,
        type: "text",
        inputMode: "decimal",
        onFocus: () => {
            focusedRef.current = true;
        },
        onKeyDown: (e: any) => {
            if (e.key === "e" || e.key === "-" || e.key === "+") {
                e.preventDefault();
            }
        },
        onChange: (e: any) => {
            const val = e.target.value;
            if (val === "") {
                setDisplay("");
                onCommit("");
                return;
            }
            if (!/^\d*\.?\d{0,2}$/.test(val)) return;
            const floatVal = parseFloat(val);
            if (isNaN(floatVal)) return;
            if (max !== undefined && floatVal > max) {
                setDisplay(max.toFixed(2));
                onCommit(max.toString());
                return;
            }
            setDisplay(val);
            onCommit(val);
        },
        onBlur: () => {
            focusedRef.current = false;
            setDisplay(format(value));
        },
    };
}

// Convenience wrapper for `number`-typed state, the more common case in this codebase.
export function useDecimalField(
    value: number,
    onValueChange: (floatVal: number) => void,
    max?: number
): DecimalInputProps {
    return useDecimalInput(
        value.toString(),
        (raw) => onValueChange(raw === "" ? 0 : Number(raw)),
        max
    );
}
